// src/components/SitemapXml.js
import { useEffect } from 'react';
import axios from '../utils/axiosHandler';

const SitemapXml = () => {
  useEffect(() => {
    // Bypass React completely and serve as XML
    const generateSitemap = async () => {
      try {
        // Remove all HTML from the page immediately
        document.documentElement.innerHTML = '';
        
        // Set the XML content type
        const meta = document.createElement('meta');
        meta.httpEquiv = 'Content-Type';
        meta.content = 'application/xml; charset=utf-8';
        document.head.appendChild(meta);
        
        // Initialize URLs array with static routes
        const urls = [
          { url: '/', changefreq: 'weekly', priority: 1.0 },
          { url: '/products', changefreq: 'daily', priority: 0.9 },
          { url: '/about', changefreq: 'monthly', priority: 0.7 },
          { url: '/contact', changefreq: 'monthly', priority: 0.7 },
          { url: '/blog', changefreq: 'daily', priority: 0.8 },
          { url: '/cart', changefreq: 'weekly', priority: 0.5 },
          { url: '/privacy-policy', changefreq: 'monthly', priority: 0.5 },
          { url: '/terms-conditions', changefreq: 'monthly', priority: 0.5 },
          { url: '/support', changefreq: 'monthly', priority: 0.6 }
        ];

        const baseUrl = 'https://bagbox.ca'; // Hard-code the production URL

        // Fetch products
        try {
          const productsRes = await axios.get('/api/products/basic');
          if (productsRes.data && Array.isArray(productsRes.data)) {
            productsRes.data.forEach(product => {
              if (!product.name) return;
              
              // Create slug using the same logic as in your navigation function
              const match = product.name.match(/^(.*?)(\d.*)?$/);
              let baseName = match[1] ? match[1].trim() : product.name;
              let sizePart = match[2] ? match[2].trim() : '';
              
              const slugBase = baseName
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9-]/g, '');
              
              const slugSize = sizePart;
              const productSlug = slugSize ? `${slugBase}-${slugSize}` : slugBase;
              
              urls.push({
                url: `/product/${productSlug}`,
                changefreq: 'weekly',
                priority: 0.8
              });
            });
          }
        } catch (err) {
          console.log('Error fetching products:', err);
        }

        // Fetch categories
        try {
          const categoriesRes = await axios.get('/api/categories/basic');
          if (categoriesRes.data && Array.isArray(categoriesRes.data)) {
            categoriesRes.data.forEach(category => {
              if (!category.name) return;
              
              const categorySlug = category.name
                .toLowerCase()
                .replace(/\s+/g, '-')
                .replace(/[^a-z0-9-]/g, '');
              
              urls.push({
                url: `/cat/${categorySlug}`,
                changefreq: 'weekly',
                priority: 0.7
              });
            });
          }
        } catch (err) {
          console.log('Error fetching categories:', err);
        }

        // Fetch blog posts
        try {
          const blogPostsRes = await axios.get('/api/blog/posts');
          if (blogPostsRes.data && Array.isArray(blogPostsRes.data)) {
            blogPostsRes.data.forEach(post => {
              if (!post.slug) return;
              
              urls.push({
                url: `/blog/${post.slug}`,
                lastmod: post.updatedAt,
                changefreq: 'monthly',
                priority: 0.7
              });
            });
          }
        } catch (err) {
          console.log('Error fetching blog posts:', err);
        }

        // Fetch blog categories
        try {
          const blogCategoriesRes = await axios.get('/api/blog/categories');
          if (blogCategoriesRes.data && Array.isArray(blogCategoriesRes.data)) {
            blogCategoriesRes.data.forEach(category => {
              if (!category.slug) return;
              
              urls.push({
                url: `/blog/category/${category.slug}`,
                changefreq: 'weekly',
                priority: 0.6
              });
            });
          }
        } catch (err) {
          console.log('Error fetching blog categories:', err);
        }

        // Fetch blog tags
        try {
          const blogTagsRes = await axios.get('/api/blog/tags');
          if (blogTagsRes.data && Array.isArray(blogTagsRes.data)) {
            blogTagsRes.data.forEach(tag => {
              if (!tag.slug) return;
              
              urls.push({
                url: `/blog/tag/${tag.slug}`,
                changefreq: 'weekly',
                priority: 0.5
              });
            });
          }
        } catch (err) {
          console.log('Error fetching blog tags:', err);
        }

        // Generate XML
        let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
        xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.sitemaps.org/schemas/sitemap/0.9 http://www.sitemaps.org/schemas/sitemap/0.9/sitemap.xsd">\n';
        xml += '<!-- created with BagBox Sitemap Generator -->\n';
        
        urls.forEach(url => {
          xml += '<url>\n';
          xml += `<loc>${baseUrl}${url.url}</loc>\n`;
          if (url.lastmod) {
            xml += `<lastmod>${new Date(url.lastmod).toISOString().split('T')[0]}</lastmod>\n`;
          }
          xml += `<changefreq>${url.changefreq}</changefreq>\n`;
          xml += `<priority>${url.priority}</priority>\n`;
          xml += '</url>\n';
        });
        
        xml += '</urlset>';

        // Clear document and write the XML directly to it
        document.open('application/xml');
        document.write(xml);
        document.close();
      } catch (error) {
        console.error('Error generating sitemap:', error);
        
        // Even on error, create a basic sitemap
        const basicXml = '<?xml version="1.0" encoding="UTF-8"?>\n' +
          '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.sitemaps.org/schemas/sitemap/0.9 http://www.sitemaps.org/schemas/sitemap/0.9/sitemap.xsd">\n' +
          '<!-- created with BagBox Sitemap Generator -->\n' +
          '<url>\n' +
          '<loc>https://bagbox.ca/</loc>\n' +
          '<changefreq>weekly</changefreq>\n' +
          '<priority>1.0</priority>\n' +
          '</url>\n' +
          '</urlset>';
          
        document.open('application/xml');
        document.write(basicXml);
        document.close();
      }
    };

    generateSitemap();
  }, []);

  // Component doesn't actually render anything via React
  return null;
};

export default SitemapXml;














// // src/components/SitemapXml.js
// import React, { useEffect, useState } from 'react';
// import { Helmet } from 'react-helmet';

// const SitemapXml = () => {
//   const [xmlContent, setXmlContent] = useState('');
//   const baseUrl = window.location.origin;

//   useEffect(() => {
//     // This function generates the XML content
//     const generateSitemap = async () => {
//       try {
//         // Initialize URLs array with static routes
//         const urls = [
//           { url: '/', changefreq: 'weekly', priority: 1.0 },
//           { url: '/products', changefreq: 'daily', priority: 0.9 },
//           { url: '/about', changefreq: 'monthly', priority: 0.7 },
//           { url: '/contact', changefreq: 'monthly', priority: 0.7 },
//           { url: '/blog', changefreq: 'daily', priority: 0.8 },
//           { url: '/cart', changefreq: 'weekly', priority: 0.5 },
//           { url: '/privacy-policy', changefreq: 'monthly', priority: 0.5 },
//           { url: '/terms-conditions', changefreq: 'monthly', priority: 0.5 },
//           { url: '/support', changefreq: 'monthly', priority: 0.6 }
//         ];

//         // Fetch products
//         try {
//           const productsRes = await axios.get('/api/products/basic');
//           if (productsRes.data && Array.isArray(productsRes.data)) {
//             productsRes.data.forEach(product => {
//               if (!product.name) return;
              
//               // Create slug using the same logic as in your navigation function
//               const match = product.name.match(/^(.*?)(\d.*)?$/);
//               let baseName = match[1] ? match[1].trim() : product.name;
//               let sizePart = match[2] ? match[2].trim() : '';
              
//               const slugBase = baseName
//                 .toLowerCase()
//                 .replace(/\s+/g, '-')
//                 .replace(/[^a-z0-9-]/g, '');
              
//               const slugSize = sizePart;
//               const productSlug = slugSize ? `${slugBase}-${slugSize}` : slugBase;
              
//               urls.push({
//                 url: `/product/${productSlug}`,
//                 changefreq: 'weekly',
//                 priority: 0.8
//               });
//             });
//           }
//         } catch (err) {
//           console.log('Error fetching products:', err);
//         }

//         // Fetch categories
//         try {
//           const categoriesRes = await axios.get('/api/categories/basic');
//           if (categoriesRes.data && Array.isArray(categoriesRes.data)) {
//             categoriesRes.data.forEach(category => {
//               if (!category.name) return;
              
//               const categorySlug = category.name
//                 .toLowerCase()
//                 .replace(/\s+/g, '-')
//                 .replace(/[^a-z0-9-]/g, '');
              
//               urls.push({
//                 url: `/cat/${categorySlug}`,
//                 changefreq: 'weekly',
//                 priority: 0.7
//               });
//             });
//           }
//         } catch (err) {
//           console.log('Error fetching categories:', err);
//         }

//         // Fetch blog posts
//         try {
//           const blogPostsRes = await axios.get('/api/blog/posts');
//           if (blogPostsRes.data && Array.isArray(blogPostsRes.data)) {
//             blogPostsRes.data.forEach(post => {
//               if (!post.slug) return;
              
//               urls.push({
//                 url: `/blog/${post.slug}`,
//                 lastmod: post.updatedAt,
//                 changefreq: 'monthly',
//                 priority: 0.7
//               });
//             });
//           }
//         } catch (err) {
//           console.log('Error fetching blog posts:', err);
//         }

//         // Fetch blog categories
//         try {
//           const blogCategoriesRes = await axios.get('/api/blog/categories');
//           if (blogCategoriesRes.data && Array.isArray(blogCategoriesRes.data)) {
//             blogCategoriesRes.data.forEach(category => {
//               if (!category.slug) return;
              
//               urls.push({
//                 url: `/blog/category/${category.slug}`,
//                 changefreq: 'weekly',
//                 priority: 0.6
//               });
//             });
//           }
//         } catch (err) {
//           console.log('Error fetching blog categories:', err);
//         }

//         // Fetch blog tags
//         try {
//           const blogTagsRes = await axios.get('/api/blog/tags');
//           if (blogTagsRes.data && Array.isArray(blogTagsRes.data)) {
//             blogTagsRes.data.forEach(tag => {
//               if (!tag.slug) return;
              
//               urls.push({
//                 url: `/blog/tag/${tag.slug}`,
//                 changefreq: 'weekly',
//                 priority: 0.5
//               });
//             });
//           }
//         } catch (err) {
//           console.log('Error fetching blog tags:', err);
//         }

//         // Generate XML
//         let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
//         xml += '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n';
        
//         urls.forEach(url => {
//           xml += '  <url>\n';
//           xml += `    <loc>${baseUrl}${url.url}</loc>\n`;
//           if (url.lastmod) {
//             xml += `    <lastmod>${new Date(url.lastmod).toISOString()}</lastmod>\n`;
//           }
//           if (url.changefreq) {
//             xml += `    <changefreq>${url.changefreq}</changefreq>\n`;
//           }
//           if (url.priority) {
//             xml += `    <priority>${url.priority}</priority>\n`;
//           }
//           xml += '  </url>\n';
//         });
        
//         xml += '</urlset>';
        
//         setXmlContent(xml);
//       } catch (error) {
//         console.error('Error generating sitemap:', error);
//         setXmlContent('<?xml version="1.0" encoding="UTF-8"?><urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9"></urlset>');
//       }
//     };

//     generateSitemap();
//   }, [baseUrl]);

//   // IMPORTANT: We need to return the XML as raw text, not as a React component
//   return (
//     <>
//       {/* Use Helmet to set content type */}
//       <Helmet>
//         <meta httpEquiv="Content-Type" content="text/xml; charset=utf-8" />
//       </Helmet>
      
//       {/* This is crucial - we're using dangerouslySetInnerHTML to render raw XML */}
//       <div 
//         dangerouslySetInnerHTML={{ __html: xmlContent }} 
//         style={{ 
//           display: 'block', 
//           visibility: 'visible',
//           fontFamily: 'monospace', 
//           whiteSpace: 'pre'
//         }} 
//       />
//     </>
//   );
// };

// export default SitemapXml;